/* eslint-disable @typescript-eslint/no-explicit-any */
import { DICOMUtils } from './dicom_utils';
import { ModalityAggregationRule } from './models/anonymization_rule';

export class AnonymizationStorage {
    private _anonymousUIDs: { [key: string]: { [key: string]: string } };
    private _originalUIDs: { [key: string]: { [key: string]: string } };

    constructor() {
        this._anonymousUIDs = {};
        this._originalUIDs = {};
    }

    public initializeDatasetAnonymization(
        dataset: any,
        aggregationRule: { [key: string]: ModalityAggregationRule },
        modelType: string
    ): [string, string] {
        let rule: ModalityAggregationRule;
        if (aggregationRule[modelType.toLowerCase()]) {
            rule = aggregationRule[modelType.toLowerCase()];
        } else {
            rule = aggregationRule['general'];
        }

        const originalPatientID = dataset.PatientID;
        const originalPatientName = dataset.PatientName[0]?.Alphabetic;

        const aggAttribute = DICOMUtils.attributeByRule(dataset, rule);
        const originalDatasetKey = `${originalPatientID}-${aggAttribute}`;

        if (this._anonymousUIDs[originalDatasetKey]) {
            const patientIDExisting =
                this._anonymousUIDs[originalDatasetKey][originalPatientID];
            let aggKey;

            if (aggAttribute) {
                aggKey = this._anonymousUIDs[originalDatasetKey][aggAttribute];
            } else {
                aggKey = this._anonymousUIDs[originalDatasetKey]['agg'];
            }

            return [originalDatasetKey, `${patientIDExisting}-${aggKey}`];
        }

        const patientID = this.generateRandomUID(8);
        const patientName =
            originalPatientID !== originalPatientName
                ? `anon-patient-${patientID}`
                : patientID; // If the patient name is the same as the ID, we can't generate a different name!
        const anonAttribute = this.generateUID();
        const datasetKey = `${patientID}-${anonAttribute}`;

        this._anonymousUIDs[originalDatasetKey] = {};

        if (aggAttribute) {
            this._anonymousUIDs[originalDatasetKey][aggAttribute] =
                anonAttribute;
        } else {
            this._anonymousUIDs[originalDatasetKey]['agg'] = anonAttribute;
        }

        this._anonymousUIDs[originalDatasetKey][originalPatientID] = patientID;
        if (originalPatientID !== originalPatientName) {
            // Don't overwrite the patient ID if it's the same as the name
            this._anonymousUIDs[originalDatasetKey][originalPatientName] =
                patientName;
        }
        this._originalUIDs[datasetKey] = {};
        this._originalUIDs[datasetKey][anonAttribute] = aggAttribute;
        this._originalUIDs[datasetKey][patientID] = originalPatientID;
        if (patientID !== patientName) {
            // Don't overwrite the patient ID if it's the same as the name
            this._originalUIDs[datasetKey][patientName] = originalPatientName;
        }
        return [originalDatasetKey, datasetKey];
    }

    public copyAnonymization(
        originalKey: string,
        datasetKey: string,
        dataset: any,
        aggregationRule: { [key: string]: ModalityAggregationRule },
        modelType: string
    ): void {
        let rule: ModalityAggregationRule;
        if (aggregationRule[modelType.toLowerCase()]) {
            rule = aggregationRule[modelType.toLowerCase()];
        } else {
            rule = aggregationRule['general'];
        }

        const patientID = dataset.PatientID;
        const aggAttribute = DICOMUtils.attributeByRule(dataset, rule);
        const newDatasetKey = `${patientID}-${aggAttribute}`;

        if (newDatasetKey !== datasetKey) {
            this._anonymousUIDs[originalKey] = {
                ...this._anonymousUIDs[originalKey],
            };
            this._originalUIDs[newDatasetKey] = {
                ...this._originalUIDs[datasetKey],
            };
        }
    }

    public anonymizeUID(
        value: string,
        originalKey: string,
        datasetKey: string
    ): string {
        if (this._anonymousUIDs[originalKey]) {
            if (this._anonymousUIDs[originalKey][value]) {
                return this._anonymousUIDs[originalKey][value];
            } else {
                const uid = this.generateUID();
                this._anonymousUIDs[originalKey][value] = uid;
                this._originalUIDs[datasetKey][uid] = value;
            }
        } else {
            this._anonymousUIDs[originalKey] = {};
            const uid = this.generateUID();
            this._anonymousUIDs[originalKey][value] = uid;
            this._anonymousUIDs[originalKey][uid] = value;
        }

        return this._anonymousUIDs[originalKey][value];
    }

    public saveOriginalMapping(
        key: string,
        originalValue: string,
        datasetKey: string
    ): void {
        this._originalUIDs[datasetKey][key] = originalValue;
    }

    public hasDataset(datasetKey: string): boolean {
        return datasetKey in this._originalUIDs;
    }

    public hasOriginalValue(key: string, datasetKey: string): boolean {
        return (
            datasetKey in this._originalUIDs &&
            key in this._originalUIDs[datasetKey]
        );
    }

    public getOriginalValue(key: string, datasetKey: string): string {
        return this._originalUIDs[datasetKey][key];
    }

    public saveAnonymizedMapping(
        key: string,
        anonValue: string,
        datasetKey: string
    ): void {
        this._anonymousUIDs[datasetKey][key] = anonValue;
    }

    private getRandomInt(min: number, max: number): number {
        min = Math.ceil(min);
        max = Math.floor(max);
        return Math.floor(Math.random() * (max - min + 1)) + min;
    }

    /**
     * Generates a portion of a random UID for DICOM use. The leading digit will never be a 0 if n has been
     * set to something other than 1.
     * @param n Number of digits to generate into UID.
     * @param isRecursive Set to true when this function loops recursively. Don't set this parameter manually.
     */
    private generateRandomUID(n: number, isRecursive = false): string {
        // generate random UID (0-9) of a fixed length
        const add = 1;
        let max = 12 - add; // 12 is the min safe number Math.random() can generate without it starting to pad the end with zeros.
        let uid: string;

        if (n > max) {
            uid =
                this.generateRandomUID(max, true) +
                this.generateRandomUID(n - max, true);
        } else {
            max = Math.pow(10, n + add);
            const min = max / 10;
            const number = Math.floor(Math.random() * (max - min + 1)) + min;
            uid = number.toString().substring(add);
        }

        if (!isRecursive && n !== 1) {
            // if we're at the root of the recursion, don't start UID with a leading zero (unless n == 1)
            return uid.charAt(0) !== '0'
                ? uid
                : this.getRandomInt(1, 9).toString() + uid.substring(1);
        } else {
            return uid;
        }
    }

    /** Generates a random UID for DICOM use. */
    public generateUID(): string {
        return '2.25.' + this.generateRandomUID(39);
    }
}
